import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  addAccessoryExercise, updateAccessoryExercise, getAllAccessoryExercises,
  getAccessoryExerciseByLibraryId,
  addAccessoryLog, getAccessoryLogsByExercise, getAllAccessoryLogs, deleteAccessoryLog,
} from '../db';
import { muscleGroups, getExercisesByGroup, getExercisesByGroupAndSub } from '../data/exerciseLibrary';
import { getCuesForExercise } from '../data/cues';
import { formatWeight } from '../utils/calculations';

const groupColors = {
  Chest: '#4a9eff',
  Back: '#4aff7f',
  Shoulders: '#ff8c4a',
  Arms: '#c77dff',
  Legs: '#ffe04a',
  Core: '#ff4a6a',
  Cardio: '#4af0f0',
  Other: '#888888',
};

// Groups that use duration-based logging instead of weight/reps/sets
const DURATION_GROUPS = ['Cardio'];

export default function AccessoriesTab() {
  const [exercises, setExercises] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [selectedEx, setSelectedEx] = useState(null);
  const [exLogs, setExLogs] = useState([]);
  const [logForm, setLogForm] = useState({ weight: '', reps: '', sets: '', duration: '' });
  const [saving, setSaving] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [activeSub, setActiveSub] = useState(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customGroup, setCustomGroup] = useState('Chest');
  const [customSub, setCustomSub] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const loadData = useCallback(async () => {
    const exs = await getAllAccessoryExercises();
    const logs = await getAllAccessoryLogs();
    setExercises(exs);
    setAllLogs(logs);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (selectedEx) {
      getAccessoryLogsByExercise(selectedEx.id).then(l => setExLogs([...l].reverse()));
    }
  }, [selectedEx, allLogs]);

  const activeExercises = useMemo(() => exercises.filter(e => e.active), [exercises]);

  // Group active exercises by muscle group
  const activeByGroup = useMemo(() => {
    const groups = [];
    const groupOrder = muscleGroups.map(g => g.name);
    const grouped = {};
    for (const ex of activeExercises) {
      const g = ex.primaryGroup || 'Other';
      if (!grouped[g]) grouped[g] = [];
      grouped[g].push(ex);
    }
    for (const name of groupOrder) {
      if (grouped[name] && grouped[name].length > 0) {
        groups.push({ group: name, exercises: grouped[name] });
      }
    }
    // Any uncategorized
    if (grouped['Other'] && grouped['Other'].length > 0) {
      groups.push({ group: 'Other', exercises: grouped['Other'] });
    }
    return groups;
  }, [activeExercises]);

  // Library ID map for quick lookup
  const libraryIdMap = useMemo(() => {
    const map = {};
    for (const ex of exercises) {
      if (ex.libraryId) map[ex.libraryId] = ex;
    }
    return map;
  }, [exercises]);

  // Latest log per exercise
  const latestLogs = useMemo(() => {
    const map = {};
    for (const log of allLogs) {
      if (!map[log.exerciseId] || log.timestamp > map[log.exerciseId].timestamp) {
        map[log.exerciseId] = log;
      }
    }
    return map;
  }, [allLogs]);

  // === Handlers ===
  const handleActivateLibraryExercise = async (libEx) => {
    const existing = await getAccessoryExerciseByLibraryId(libEx.id);
    if (existing) {
      await updateAccessoryExercise({ ...existing, active: true });
    } else {
      await addAccessoryExercise({
        libraryId: libEx.id,
        name: libEx.name,
        primaryGroup: libEx.primaryGroup,
        primarySub: libEx.primarySub,
      });
    }
    loadData();
  };

  const handleDeactivate = async (ex) => {
    await updateAccessoryExercise({ ...ex, active: false });
    setSelectedEx(null);
    loadData();
  };

  const handleAddCustom = async () => {
    const name = customName.trim();
    if (!name) return;
    const group = muscleGroups.find(g => g.name === customGroup);
    const sub = customSub || (group ? group.subs[0] : '');
    await addAccessoryExercise({
      libraryId: null,
      name,
      primaryGroup: customGroup,
      primarySub: sub,
    });
    setCustomName('');
    setShowCustomForm(false);
    loadData();
  };

  const handleLogSet = async () => {
    if (!selectedEx || saving) return;
    const isDuration = DURATION_GROUPS.includes(selectedEx.primaryGroup);
    setSaving(true);
    try {
      await addAccessoryLog({
        exerciseId: selectedEx.id,
        exerciseName: selectedEx.name,
        weight: isDuration ? 0 : (parseFloat(logForm.weight) || 0),
        reps: isDuration ? 0 : (parseInt(logForm.reps) || 0),
        sets: isDuration ? 0 : (parseInt(logForm.sets) || 1),
        duration: isDuration ? (parseInt(logForm.duration) || 0) : 0,
      });
      setLogForm({ weight: '', reps: '', sets: '', duration: '' });
      loadData();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedEx) return;
    await updateAccessoryExercise({ ...selectedEx, notes: noteDraft });
    setSelectedEx({ ...selectedEx, notes: noteDraft });
    setEditingNotes(false);
    loadData();
  };

  const handleDeleteLog = async (id) => {
    await deleteAccessoryLog(id);
    setConfirmDelete(null);
    loadData();
  };

  // === Exercise Logging Screen ===
  if (selectedEx) {
    const isDurationEx = DURATION_GROUPS.includes(selectedEx.primaryGroup);
    return (
      <div>
        <button className="btn btn-secondary btn-small" style={{ marginBottom: 12, width: 'auto' }}
          onClick={() => { setSelectedEx(null); setEditingNotes(false); }}>
          ← Back
        </button>

        <div className="session-header" style={{ paddingTop: 0 }}>
          <h1>{selectedEx.name}</h1>
          <p className="sub">{selectedEx.primaryGroup} · {selectedEx.primarySub}</p>
        </div>

        {/* Built-in form cues */}
        {(() => {
          const cues = getCuesForExercise(selectedEx.name);
          if (!cues) return null;
          return (
            <div className="card" style={{ padding: 14, marginBottom: 12 }}>
              <div className="card-header" style={{ marginBottom: 8 }}>Form Cues</div>
              {cues.map((c, i) => (
                <div key={i} style={{ fontSize: 13, color: 'var(--text-dim)', padding: '3px 0', lineHeight: 1.45 }}>
                  · {c}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Notes */}
        <div className="card" style={{ padding: 14, marginBottom: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span className="card-header" style={{ marginBottom: 0 }}>Notes</span>
            {!editingNotes && (
              <button style={{
                background: 'none', border: 'none', color: 'var(--accent)',
                fontSize: 12, cursor: 'pointer', fontFamily: 'Barlow Condensed, sans-serif',
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }} onClick={() => { setEditingNotes(true); setNoteDraft(selectedEx.notes || ''); }}>
                {selectedEx.notes ? 'Edit' : 'Add'}
              </button>
            )}
          </div>
          {editingNotes ? (
            <div>
              <textarea value={noteDraft} onChange={e => setNoteDraft(e.target.value)}
                placeholder="e.g. Don't lean too far forward" style={{ marginBottom: 8 }} />
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-small" style={{ flex: 1 }} onClick={handleSaveNotes}>Save</button>
                <button className="btn btn-secondary btn-small" style={{ flex: 1 }} onClick={() => setEditingNotes(false)}>Cancel</button>
              </div>
            </div>
          ) : selectedEx.notes ? (
            <div style={{ fontSize: 14, color: 'var(--text-dim)', fontStyle: 'italic', lineHeight: 1.5 }}>
              {selectedEx.notes}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No notes yet</div>
          )}
        </div>

        {/* Log form */}
        <div className="card" style={{ padding: 14 }}>
          <div className="card-header">{isDurationEx ? 'Log Session' : 'Log Set'}</div>
          {isDurationEx ? (
            <div>
              <label>Duration (minutes)</label>
              <input type="number" value={logForm.duration}
                onChange={e => setLogForm(p => ({ ...p, duration: e.target.value }))}
                onFocus={e => e.target.select()} placeholder="e.g. 30" />
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <label>Weight</label>
                <input type="number" value={logForm.weight}
                  onChange={e => setLogForm(p => ({ ...p, weight: e.target.value }))}
                  onFocus={e => e.target.select()} placeholder="kg" />
              </div>
              <div style={{ flex: 1 }}>
                <label>Reps</label>
                <input type="number" value={logForm.reps}
                  onChange={e => setLogForm(p => ({ ...p, reps: e.target.value }))}
                  onFocus={e => e.target.select()} placeholder="reps" />
              </div>
              <div style={{ flex: 1 }}>
                <label>Sets</label>
                <input type="number" value={logForm.sets}
                  onChange={e => setLogForm(p => ({ ...p, sets: e.target.value }))}
                  onFocus={e => e.target.select()} placeholder="1" />
              </div>
            </div>
          )}
          <button className="btn btn-primary" style={{ marginTop: 12 }}
            onClick={handleLogSet} disabled={saving || (isDurationEx ? !logForm.duration : (!logForm.reps && !logForm.weight))}>
            {saving ? 'Saving...' : 'Log'}
          </button>
        </div>

        {/* History */}
        <div className="card-header" style={{ marginTop: 12 }}>History</div>
        {exLogs.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No logs yet</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead><tr>
                <th>Date</th>
                {isDurationEx ? <th>Duration</th> : <><th>Weight</th><th>Reps</th><th>Sets</th></>}
                <th></th>
              </tr></thead>
              <tbody>
                {exLogs.map(log => (
                  <tr key={log.id}>
                    <td>{fmtDate(log.date)}</td>
                    {isDurationEx ? (
                      <td>{log.duration ? `${log.duration} min` : '—'}</td>
                    ) : (
                      <>
                        <td>{log.weight > 0 ? `${formatWeight(log.weight)}kg` : '—'}</td>
                        <td>{log.reps || '—'}</td>
                        <td>{log.sets || 1}</td>
                      </>
                    )}
                    <td>
                      <button style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13, cursor: 'pointer', padding: 4 }}
                        onClick={() => setConfirmDelete(log.id)}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <button className="btn btn-danger" style={{ marginTop: 20 }} onClick={() => handleDeactivate(selectedEx)}>
          Remove from Active
        </button>

        {confirmDelete && (
          <div className="modal-overlay" onClick={() => setConfirmDelete(null)}>
            <div className="confirm-dialog" onClick={e => e.stopPropagation()}>
              <h3>Delete Log Entry?</h3>
              <p>This cannot be undone.</p>
              <div className="btn-row">
                <button className="btn btn-secondary" onClick={() => setConfirmDelete(null)}>Cancel</button>
                <button className="btn btn-danger" onClick={() => handleDeleteLog(confirmDelete)}>Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // === Main Screen ===
  const libraryExercises = expandedGroup
    ? (activeSub ? getExercisesByGroupAndSub(expandedGroup, activeSub) : getExercisesByGroup(expandedGroup))
    : [];

  return (
    <div>
      <div className="session-header">
        <h1>Accessories</h1>
        <p className="sub">{activeExercises.length} active exercise{activeExercises.length !== 1 ? 's' : ''}</p>
      </div>

      {/* Active exercises grouped by muscle group */}
      {activeByGroup.length > 0 ? (
        activeByGroup.map(grp => (
          <div key={grp.group} style={{ marginBottom: 16 }}>
            <div className="acc-group-label" style={{ color: groupColors[grp.group] || 'var(--text-dim)' }}>
              {grp.group}
            </div>
            <div className="acc-grid">
              {grp.exercises.map(ex => {
                const latest = latestLogs[ex.id];
                const isDur = DURATION_GROUPS.includes(ex.primaryGroup);
                return (
                  <div key={ex.id} className="acc-tile" onClick={() => setSelectedEx(ex)}>
                    <div className="acc-tile-name">{ex.name}</div>
                    <div className="acc-tile-sub">{ex.primarySub}</div>
                    {latest && (
                      <div className="acc-tile-last">
                        {isDur
                          ? (latest.duration ? `${latest.duration} min` : '')
                          : (<>
                              {latest.weight > 0 ? `${formatWeight(latest.weight)}kg` : ''}
                              {latest.weight > 0 && latest.reps ? ' × ' : ''}
                              {latest.reps ? `${latest.reps}` : ''}
                            </>)
                        }
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      ) : (
        <div style={{ textAlign: 'center', padding: '16px 0 24px', color: 'var(--text-muted)', fontSize: 13 }}>
          No active accessories — browse below to add some
        </div>
      )}

      {/* Divider */}
      <div className="section-divider" style={{ marginTop: 8, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
        Exercise Library
      </div>

      {/* Custom exercise */}
      {!showCustomForm ? (
        <button className="btn btn-secondary btn-small" style={{ marginBottom: 12, width: 'auto' }}
          onClick={() => setShowCustomForm(true)}>+ Custom Exercise</button>
      ) : (
        <div className="card" style={{ padding: 14, marginBottom: 12 }}>
          <div className="card-header">Add Custom Exercise</div>
          <input type="text" value={customName} onChange={e => setCustomName(e.target.value)}
            placeholder="Exercise name" style={{ marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <div style={{ flex: 1 }}>
              <label>Muscle Group</label>
              <select value={customGroup} onChange={e => { setCustomGroup(e.target.value); setCustomSub(''); }}>
                {muscleGroups.map(g => <option key={g.name} value={g.name}>{g.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label>Sub-group</label>
              <select value={customSub} onChange={e => setCustomSub(e.target.value)}>
                <option value="">—</option>
                {(muscleGroups.find(g => g.name === customGroup)?.subs || []).map(s =>
                  <option key={s} value={s}>{s}</option>
                )}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-small" style={{ flex: 1 }} onClick={handleAddCustom} disabled={!customName.trim()}>Add</button>
            <button className="btn btn-secondary btn-small" style={{ flex: 1 }} onClick={() => setShowCustomForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Muscle group library */}
      {muscleGroups.map(group => {
        const isExpanded = expandedGroup === group.name;
        return (
          <div key={group.name} style={{ marginBottom: 6 }}>
            <div className="acc-group-header" onClick={() => {
              setExpandedGroup(isExpanded ? null : group.name);
              setActiveSub(null);
            }}>
              <span className="acc-group-name" style={{ color: groupColors[group.name] || 'var(--text)' }}>{group.name}</span>
              <span className="acc-group-count">{getExercisesByGroup(group.name).length}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 16, marginLeft: 'auto' }}>{isExpanded ? '−' : '+'}</span>
            </div>

            {isExpanded && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 0' }}>
                  <button className={`acc-chip ${!activeSub ? 'active' : ''}`} onClick={() => setActiveSub(null)}>All</button>
                  {group.subs.map(sub => (
                    <button key={sub} className={`acc-chip ${activeSub === sub ? 'active' : ''}`}
                      onClick={() => setActiveSub(activeSub === sub ? null : sub)}>{sub}</button>
                  ))}
                </div>

                {libraryExercises.map(libEx => {
                  const isActive = !!libraryIdMap[libEx.id]?.active;
                  return (
                    <div key={libEx.id} className={`acc-lib-item ${isActive ? 'active' : ''}`}
                      onClick={() => { if (!isActive) handleActivateLibraryExercise(libEx); }}>
                      <div style={{ flex: 1 }}>
                        <div className="acc-lib-name">{libEx.name}</div>
                        {libEx.groups.filter(g => g.group !== expandedGroup).length > 0 && (
                          <div className="acc-lib-tags">
                            Also: {libEx.groups.filter(g => g.group !== expandedGroup).map(g => g.sub).join(', ')}
                          </div>
                        )}
                      </div>
                      {isActive ? <span className="acc-active-badge">Active</span> : <span className="acc-add-btn">+</span>}
                    </div>
                  );
                })}

                {/* Custom exercises in this group */}
                {exercises.filter(e => !e.libraryId && e.primaryGroup === group.name && (!activeSub || e.primarySub === activeSub)).map(ex => (
                  <div key={ex.id} className={`acc-lib-item ${ex.active ? 'active' : ''}`}
                    onClick={() => { if (!ex.active) updateAccessoryExercise({ ...ex, active: true }).then(loadData); }}>
                    <div style={{ flex: 1 }}>
                      <div className="acc-lib-name">{ex.name} <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>custom</span></div>
                      <div className="acc-lib-tags">{ex.primarySub}</div>
                    </div>
                    {ex.active ? <span className="acc-active-badge">Active</span> : <span className="acc-add-btn">+</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function fmtDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
